import React, { useContext, useEffect, useRef } from "react";
import { List, ListItem, Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { VehicleAvatar, LegalEntityAvatar, Tag, PersonAvatar, VehicleInstalledTermometer } from "../Snippet";
import { VehicleKilometer } from "../Chart";
import { Icon } from "../../assets";
import { useDrawer } from "../Drawer";
import { useDataProvider } from '../../../mtska-frontend-data-provider';
import { useFormatter } from "src/mtska-frontend-app-component/utils";
import { Text } from "../Typography";
import { AppContext } from "src/mtska-frontend-app-scaffolding";

const InfoHeaderVehicle = ({ view, item, hideContractBox = false }) => {
    const { t } = useTranslation();
    const { activate } = useDrawer();
    const { formatDate, formatDistance } = useFormatter();
    const { appConfiguration, userCapabilities } = useContext(AppContext);

    const { loadOne } = useDataProvider('cars/car');
    const handleRefineContract = () => {
        !userCapabilities['auth/role.is_driver'] && activate('contracts/detail', {
            id: item?.contract?._id,
            defaultValue: item?.contract,
            filters: item?.contract,
        }, () => loadOne(item?._id));
    };

    const handleAssociateContract = () => {
        activate('cars/contractAssociation', {
            id: item?._id,
            defaultValue: item,
            filters: item,
        });
    };

    const handleAssociatePerson = () => {
        activate('cars/personAssociation', {
            id: item?.id,
            defaultValue: item,
            filters: item,
        });
    };

    const tracingSerie = useRef();
    const tracingSerieRedraw = useRef(0);
    useEffect(() => {
        if (!item) return;
        if (item.traceKms != tracingSerie.current) {
            tracingSerie.current = item.traceKms
            tracingSerieRedraw.current = tracingSerieRedraw.current +1
        }
    }, [item])

    return (
        <Box className={"info-header info-header-" + view}>
            <VehicleAvatar size="big" url={item?.picture?.url} item={item} />
            <Box className={"info-wrapper"}>
                <Box className="info-summary-external">
                    <LegalEntityAvatar name={item?.legalEntity?.name} url={item?.legalEntity?.logo?.url} size="small" withname={true} />

                    {
                        item?.contract ? (
                            <LegalEntityAvatar name={item?.contract?.renter?.name} url={item?.contract?.renter?.logo?.url} size="small" withname={true} error={(!item?.contract?.start_at || !item?.contract?.end_at) && t("Missing contract's start and/or end date")} onClick={handleRefineContract} />
                        ) : (
                            <LegalEntityAvatar name={item?.contract?.renter?.name} url={item?.contract?.renter?.logo?.url} size="small" withname={true} error={t('No contract assigned')} onClick={handleAssociateContract} />
                        )
                    }

                    {
                        item?.contract?.start_at && (
                            <Text icon="faArrowRightFromLine" className="info-contract">{formatDate(item?.contract?.start_at)}</Text>
                        )
                    }

                    {
                        item?.contract?.end_at && (
                            <Text icon="faArrowRightToLine" className="info-contract">{formatDate(item?.contract?.end_at)}</Text>
                        )
                    }

                    {
                        item?.contract?.contractDuration && (
                            <Text icon="faHourglass" className="info-contract">{item?.contract?.contractDuration} {t('months')}</Text>
                        )
                    }

                    {
                        item?.contract?.contractMileage && (
                            <Text icon="faRoad" className="info-contract">{formatDistance(item?.contract?.contractMileage)}</Text>
                        )
                    }

                </Box>
                <Box className="info-name">
                    <span className="primary">{item?.setup?.make ?? t('Unknown make, model, setup')} {item?.setup?.model}</span><span className="secondary" title={item?.setup?.setup}>{item?.setup?.setup}</span>
                </Box>
                <List className="info-details">
                    <ListItem>
                        <span className="key">{t('plate')}</span>
                        <span className="value">{item?.plate || (<Tag color={"warning"} title={t("No plate yet assigned")}><Icon icon="faWarning" />{t('No plate yet assigned')}</Tag>)}</span>
                    </ListItem>
                    {
                        !userCapabilities['auth/role.is_driver'] && (
                            <ListItem>
                                <span className="key">{t('order')}</span>
                                <span className="value">{item?.contract?.orderIdentifier || (<Tag color={"danger"} title={t("Missing contract's order identifier")} onClick={item?.contract ? handleRefineContract : handleAssociateContract}><Icon icon="faWarning" />{t('Missing')}</Tag>)}</span>
                            </ListItem>
                        )
                    }
                    <ListItem>
                        <span className="key">{t('status')}</span>
                        <Box className="value">
                            {
                                item?.status ? (<Tag color={"secondary"} size="small" label={t(item?.status)} />) : (<Tag color={"danger"} title={t("Missing vehicle status")}><Icon icon="faWarning" />{t('Missing')}</Tag>)
                            }
                        </Box>
                    </ListItem>
                    <ListItem>
                        <span className="key">{t('contractFormula')}</span>
                        <span className="value">{t(item?.contract?.contractFormula) || (<Tag color={"danger"} title={t("Missing contract's formula")} onClick={item?.contract ? handleRefineContract : handleAssociateContract}><Icon icon="faWarning" />{t('Missing')}</Tag>)}</span>
                    </ListItem>
                    {
                        !userCapabilities['auth/role.is_driver'] && !appConfiguration.dashboard.view == 'ConsecutionSales' && (
                            <ListItem>
                                <span className="key">
                                    {
                                        (item?.isPool == "true") ? (
                                            t('poolmembers')
                                        ) : (
                                            t('assignee')
                                        )
                                    }
                                </span>
                                <Box className="value">
                                    {
                                        Array.isArray(item?.pools) && (item?.pools.length == 1) && (
                                            <PersonAvatar
                                                item={item.pools[0].employment?.person}
                                                firstname={item.pools[0].employment?.person?.firstname || item.pools[0].employment?.person?.name}
                                                lastname={item.pools[0].employment?.person?.lastname}
                                                size="small"
                                                url={item.pools[0].employment?.person?.picture?.url}
                                                withname={true}
                                                detail={(item.pools[0].employment?.person?.email ? item.pools[0].employment?.person?.email : '') + " " + (item.pools[0].employment?.person?.mobile ? item.pools[0].employment?.person?.mobile : '')}
                                            />
                                        )
                                    }

                                    {
                                        Array.isArray(item?.pools) && (item?.pools?.length > 1) && (
                                            item?.pools?.slice(0, 5).map((pool, k) => (
                                                <PersonAvatar firstname={pool.employment?.person?.firstname || pool.employment?.person?.name} lastname={pool.employment?.person?.lastname} size="small" url={pool.employment?.person?.picture?.url} item={pool.employment?.person} />
                                            ))
                                        )
                                    }

                                    {
                                        Array.isArray(item?.pools) && (item?.pools?.length > 5) && (
                                            <span>+{item?.pools?.length - 5}</span>
                                        )
                                    }

                                    {
                                        (!Array.isArray(item?.pools)) && (
                                            <Tag color={"danger"} title={t("Missing drivers in pool")} onClick={handleAssociatePerson}><Icon icon="faWarning" />{t('Missing')}</Tag>
                                        )
                                    }

                                </Box>
                            </ListItem>
                        )
                    }
                </List>
            </Box>
            {/* 
            contractStartDate={item?.contract?.start_at}<br/>
            contractEndDate={item?.contract?.end_at}<br/>
            contractTotalKM={item?.contract?.contractMileage}<br/>
            lastTraceKMValue={item?.lastTraceKMValue}<br/>
            lastTraceKMDate={item?.lastTraceKMDate}<br/>
            contractCostKMExcess={item?.contract?.bracket1ExtraKmCost}<br/>
            contractMonths={item?.contract?.contractDuration}
             */}
            {
                (!hideContractBox && item?.contract && item?.contract?.start_at && item?.contract?.end_at) && (
                    <>
                        <VehicleInstalledTermometer
                            item={item}
                            contractStartDate={item?.contract?.start_at}
                            contractEndDate={item?.contract?.end_at}
                            contractMonths={item?.contract?.contractDuration}
                            contractInitialKM={item?.contract?.initialMileage}
                            contractTotalKM={item?.contract?.contractMileage}
                            lastTraceKMValue={item?.traceKmsLast?.value}
                            lastTraceKMDate={item?.traceKmsLast?.traced_at}
                            layout="aside"
                        />
                        <VehicleKilometer
                            item={item}
                            contractStartDate={item?.contract?.start_at}
                            contractEndDate={item?.contract?.end_at}
                            contractMonths={item?.contract?.contractDuration}
                            contractKilometers={item?.contract?.contractMileage}
                            contractCostKMExcess={item?.contract?.bracket1ExtraKmCost}
                            contractKMReimbursement={item?.contract?.bracket1KmReimbursement}
                            contractPercentage1={item?.contract?.bracket1Percentage}
                            contractCostKMExcess2={item?.contract?.bracket2ExtraKmCost}
                            contractKMReimbursement2={item?.contract?.bracket2KmReimbursement}
                            contractPercentage2={item?.contract?.bracket2Percentage}
                            contractCostKMExcess3={item?.contract?.bracket3ExtraKmCost}
                            contractKMReimbursement3={item?.contract?.bracket3KmReimbursement}
                            contractPercentage3={item?.contract?.bracket3Percentage}
                            contractCostKMCurrency={'EUR'}
                            contractKmDeductible={item?.contract?.contractKmDeductible}
                            contractInitialMileage={item?.contract?.initialMileage}
                            contractMaxReimbursementAmount={item?.contract?.maxReimbursementAmount}
                            tracingSerie={tracingSerie.current}
                            tracingSerieRedraw={tracingSerieRedraw.current}
                        />
                    </>
                )
            }

        </Box>
    )
}

export default InfoHeaderVehicle;